import { useRouter } from "next/router";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { useMutation } from "@apollo/client";
import { CLOSE_LISTING } from "@/graphql/queries/listing";
import { DropDown, Modal } from "@aqar-app/react-ui";
import css from "@/components/Listing/ListingScreen/.module.scss";
import Link from "next/link";
import React from "react";
import { ClassicModal } from "@/components/Listing/ListingScreen/classicModal";
import Icon from "@/components/icon";
import {
  trackEditListingComplete,
  trackStartEditListing,
  trackEditAdOptions,
  trackSelectEditOption,
  trackSaveDiscount,
  trackViewSuccessDiscount,
  trackCancelDiscount,
  trackViewCancelDiscount,
} from "@/lib/tracking/trackEditListing";
import { useListingStatusControl } from "@/hooks/useListingStatusControl";
import Input from "@/components/input/input";
import { Button } from "@aqar-app/react-ui";
import { UPDATE_REGA_LISTING_PRICES } from "@/graphql/mutations/listing";
import { usePath } from "@/contexts/path/path";

export function OwnerControls({ listing } = {}) {
  const router = useRouter();
  const { previousPath, categoriesDictionary } = usePath();
  const { user, token } = useAuthState();
  const [closeListing] = useMutation(CLOSE_LISTING, {
    context: { token },
  });
  const authUserId = user?.user_id;

  const isPaused = listing?.status === 5;

  const handleEditOptionsClick = () => {
    const referrer =
      typeof window !== "undefined" ? window.document?.referrer : "";
    trackEditAdOptions({
      source: previousPath || decodeURI(referrer || ""),
      listingType: categoriesDictionary?.[listing?.category]?.ga_listing_type,
      propertyCategory:
        categoriesDictionary?.[listing?.category]?.ga_property_category,
    });
  };

  const handleSelectEditOption = (target) => {
    const referrer =
      typeof window !== "undefined" ? window.document?.referrer : "";
    trackSelectEditOption({
      source: previousPath || decodeURI(referrer || ""),
      listingType: categoriesDictionary?.[listing?.category]?.ga_listing_type,
      propertyCategory:
        categoriesDictionary?.[listing?.category]?.ga_property_category,
      target,
    });
  };

  const handleCloseListing = (e, { id }) => {
    Modal.open(
      <ClassicModal
        ok={{
          label: "نعم",
          cb: (e) => {
            ClassicModal.loading("ok")(e);
            closeListing({
              variables: {
                id,
              },
            })
              .then(() => {
                trackEditListingComplete();
                Modal.open(
                  <ClassicModal
                    ok={{
                      label: "حسنا",
                      cb: (e) =>
                        router
                          .push(`/user/${authUserId}`)
                          .then(() => Modal.close(e)),
                    }}
                    body={<p>{`تم حذف إعلان رقم (${id}) بنجاح.`}</p>}
                  />,
                )(e);
              })
              .catch((error) => {
                Modal.open(
                  <ClassicModal
                    ok={{
                      label: "حسنا",
                      cb: (e) => Modal.close(e),
                    }}
                    body={
                      <p>
                        {error.message ||
                          "فشل في حذف الإعلان، الرجاء المحاولة لاحقاً."}
                      </p>
                    }
                  />,
                )(e);
              });
          },
        }}
        body={<p>هل أنت متأكد من حذف هذا الإعلان؟</p>}
        title="حذف الإعلان"
      />,
    )(e);
  };

  const openSuccessModal = () => {
    Modal.open(
      <ClassicModal
        newDesign
        close={false}
        cancel={false}
        title="عقار"
        ok={{
          label: "حسنًا",
          cb: (e) => {
            Modal.close(e);
            router.reload();
          },
        }}
        body={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <p style={{ maxWidth: "356px", textAlign: "center" }}>
              تم حفظ التعديلات بنجاح
            </p>
          </div>
        }
      />,
    )();
  };

  const { handlePauseListing } = useListingStatusControl({
    token,
    onSuccess: () => {
      openSuccessModal();
    },
    id: listing.id,
  });

  const openDiscountModal = (e) => {
    const DiscountModal = () => {
      const isMeterBasedCategory =
        listing?.category === 2 || listing?.category === 15;
      const original = isMeterBasedCategory
        ? listing?.rega_meter_price || 0
        : listing?.rega_total_price || 0;
      const currentListedPrice = isMeterBasedCategory
        ? listing?.meter_price || 0
        : listing?.price || 0;
      const initialDiscounted = isMeterBasedCategory
        ? currentListedPrice || original
        : currentListedPrice || original;
      const [discountedPrice, setDiscountedPrice] = React.useState(
        original === currentListedPrice ? "" : initialDiscounted,
      );
      const [attemptedSave, setAttemptedSave] = React.useState(false);

      const [updatePrices, { loading: updateLoading }] = useMutation(
        UPDATE_REGA_LISTING_PRICES,
        {
          context: { token },
        },
      );

      const discountPercent =
        original > 0 ? ((original - discountedPrice) / original) * 100 : 0;

      const hasDiscount =
        original > 0 &&
        ((isMeterBasedCategory &&
          listing?.rega_meter_price !== undefined &&
          listing?.meter_price !== undefined &&
          listing?.rega_meter_price !== listing?.meter_price) ||
          (!isMeterBasedCategory &&
            listing?.rega_total_price !== undefined &&
            listing?.price !== undefined &&
            listing?.rega_total_price !== listing?.price));

      const isValid =
        original > 0 &&
        discountedPrice > 0 &&
        discountedPrice < original &&
        discountPercent >= 1;

      let helperMessage = "";
      if (original <= 0) helperMessage = "السعر غير متوفر";
      else if (discountedPrice >= original)
        helperMessage = "يجب أن يكون السعر بعد الخصم أقل من السعر حسب الرخصة";
      else if (discountPercent < 1)
        helperMessage = "يجب أن يكون الخصم على الأقل 1%";
      else if (discountedPrice <= 0) helperMessage = "أدخل سعرًا صالحًا";

      const handleSave = (btnEvent) => {
        if (!isValid) {
          setAttemptedSave(true);
          return;
        }
        trackSaveDiscount({
          source: previousPath || decodeURI(document.referrer),
          listingType:
            categoriesDictionary?.[listing?.category]?.ga_listing_type,
          propertyCategory:
            categoriesDictionary?.[listing?.category]?.ga_property_category,
          oldPrice: original,
          newPrice: discountedPrice,
        });
        updatePrices({
          variables: {
            listing_id: listing.id,
            ...(isMeterBasedCategory
              ? { meter_price: discountedPrice }
              : { total_price: discountedPrice }),
          },
        })
          .then(() => {
            Modal.close(btnEvent);
            trackViewSuccessDiscount({
              source: previousPath || decodeURI(document.referrer),
              listingType:
              categoriesDictionary?.[listing?.category]?.ga_listing_type,
              propertyCategory:
              categoriesDictionary?.[listing?.category]?.ga_property_category,
              oldPrice: original,
              newPrice: discountedPrice,
            });
            Modal.open(
              <ClassicModal
                newDesign
                close={false}
                cancel={false}
                title="عقار"
                ok={{
                  label: "حسنًا",
                  cb: (closeEvent) => {
                    Modal.close(closeEvent);
                    router.reload();
                  },
                }}
                body={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <p
                      style={{
                        maxWidth: "356px",
                        textAlign: "center",
                      }}
                    >
                      تم حفظ التعديل بنجاح
                    </p>
                  </div>
                }
              />,
            )();
          })
          .catch((error) => {
            Modal.close(btnEvent);
            Modal.open(
              <ClassicModal
                newDesign
                close={false}
                cancel={false}
                title="عقار"
                ok={{
                  label: "حسنًا",
                  cb: (closeEvent) => {
                    Modal.close(closeEvent);
                  },
                }}
                body={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <p
                      style={{
                        maxWidth: "356px",
                        textAlign: "center",
                        color: "#d9534f",
                      }}
                    >
                      {error.message ||
                        "فشل حفظ سعر الخصم، الرجاء المحاولة لاحقاً."}
                    </p>
                  </div>
                }
              />,
            )();
          });
      };

      const handleCancelDiscount = (e) => {
        trackCancelDiscount({
          source: previousPath || decodeURI(document.referrer),
          listingType:
          categoriesDictionary?.[listing?.category]?.ga_listing_type,
          propertyCategory:
          categoriesDictionary?.[listing?.category]?.ga_property_category
        });
        Modal.open(
          <ClassicModal
            newDesign
            close={false}
            title="تأكيد إلغاء الخصم"
            body={
              <p style={{ textAlign: "center" }}>
                هل أنت متأكد من إلغاء الخصم وإعادة السعر حسب الرخصة؟
              </p>
            }
            ok={{
              label: "نعم",
              cb: async (okEvent) => {
                trackViewCancelDiscount({
                  source: previousPath || decodeURI(document.referrer),
                  listingType:
                  categoriesDictionary?.[listing?.category]?.ga_listing_type,
                  propertyCategory:
                  categoriesDictionary?.[listing?.category]?.ga_property_category,
                  inputData: "yes"
                });
                ClassicModal.loading("ok")(okEvent);
                await updatePrices({
                  variables: {
                    listing_id: listing.id,
                    ...(isMeterBasedCategory
                      ? { meter_price: listing?.rega_meter_price }
                      : { total_price: listing?.rega_total_price }),
                  },
                });
                Modal.close(okEvent);
                router.reload();
              },
            }}
            cancel={{
              label: "لا",
              cb: (cancelEvent) => {
                trackViewCancelDiscount({
                  source: previousPath || decodeURI(document.referrer),
                  listingType:
                  categoriesDictionary?.[listing?.category]?.ga_listing_type,
                  propertyCategory:
                  categoriesDictionary?.[listing?.category]?.ga_property_category,
                  inputData: "no"
                });
                Modal.close(cancelEvent)
              },
            }}
          />,
        )(e);
      };

      return (
        <ClassicModal
          newDesign
          title="تحديد سعر الخصم"
          closeButtonStyle={{ top: "12px", left: "12px", position: "absolute" }}
          body={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    color: "#41474E",
                    textAlign: "right",
                  }}
                >
                  السعر حسب الرخصة
                </label>
                <Input
                  className={css.disabledInput}
                  disabled
                  type="number"
                  value={original}
                  readOnly
                  style={{ direction: "rtl", background: "#F9FAFB" }}
                  suffix={<span className={css.sarSuffix}>ريال</span>}
                />
                {isMeterBasedCategory && (
                  <span style={{ textAlign: "right" }}>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#66717E",
                      }}
                    >
                      السعر الإجمالي الحالي:{" "}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#008236",
                      }}
                    >
                      {listing?.rega_total_price} ريال
                    </span>
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#41474E",
                      textAlign: "right",
                    }}
                  >
                    السعر الجديد
                  </p>
                  {hasDiscount && (
                    <div>
                      <button
                        style={{
                          padding: 0,
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#EB605A",
                        }}
                        disabled={updateLoading}
                        onClick={() => {
                          Modal.close();
                          handleCancelDiscount();
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.7144 13.8149L7.25031 9.35066L2.78628 13.8149C2.20575 14.3949 1.26685 14.3952 0.685394 13.8149C0.105802 13.2354 0.104869 12.294 0.685394 11.714L5.14943 7.25L0.685394 2.78596C0.104869 2.20562 0.104869 1.26495 0.685394 0.685079C1.26499 0.105207 2.20575 0.10474 2.78628 0.685079L7.25031 5.14912L11.7144 0.685079C12.2939 0.105114 13.2356 0.10558 13.8152 0.685079C14.3948 1.26542 14.3948 2.2059 13.8152 2.78596L9.3512 7.25L13.8152 11.714C14.3948 12.2944 14.3948 13.235 13.8152 13.8149C13.2356 14.3948 12.2939 14.3952 11.7144 13.8149Z"
                            fill="#EB605A"
                            stroke="white"
                            strokeWidth="0.5"
                          />
                        </svg>
                        إلغاء الخصم
                      </button>
                    </div>
                  )}
                </label>
                <Input
                  className={css.discountInput}
                  type="number"
                  min={0}
                  value={discountedPrice}
                  onChange={(ev) => {
                    setAttemptedSave(false);
                    const v = Number(ev.target.value);
                    setDiscountedPrice(Number.isNaN(v) ? 0 : v);
                  }}
                  placeholder="أدخل السعر بعد الخصم"
                  style={{ direction: "rtl" }}
                  suffix={<span className={css.sarSuffix}>ريال</span>}
                />
                {isMeterBasedCategory && (
                  <span style={{ textAlign: "right" }}>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#66717E",
                      }}
                    >
                      السعر الإجمالي الجديد:{" "}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#008236",
                      }}
                    >
                      {discountedPrice
                        ? "ريال " + discountedPrice * listing?.area
                        : ""}
                    </span>
                  </span>
                )}
                <small style={{ color: "#66717E", textAlign: "right" }}>
                  * لا يمكن زيادة سعر أعلى من الموجود في الرخصة إلا بإصدار ترخيص
                  إعلان جديد.
                </small>
                {attemptedSave && helperMessage && !isValid && (
                  <small style={{ color: "#d9534f", textAlign: "right" }}>
                    {helperMessage}
                  </small>
                )}
              </div>
            </div>
          }
          ok={{
            jsx: (
              <Button
                theme="primary"
                disabled={updateLoading} // Only disable while loading
                onClick={handleSave}
              >
                حفظ
              </Button>
            ),
          }}
          cancel={false}
        />
      );
    };

    Modal.open(<DiscountModal />)(e);
  };

  return (
    <DropDown
      zIndex={100}
      overlay={
        <div className={css.dropdown}>
          {!listing?.daily_rentable && !!listing.rega_licensed && (
            <button
              onClick={(e) => {
                handleSelectEditOption("specify_discount_price");
                openDiscountModal(e);
              }}
            >
              تحديد سعر الخصم
            </button>
          )}
          <Link
            href={`/edit-listing/v2/${listing.id}?type=media`}
            passHref
            onClick={() => {
              handleSelectEditOption("modify_photos");
              trackStartEditListing({ target: "edit_image" });
            }}
          >
            تعديل الصور
          </Link>
          <Link
            href={`/edit-listing/v2/${listing.id}?type=location`}
            passHref
            onClick={() => {
              handleSelectEditOption("modify_location");
              trackStartEditListing({ target: "edit_location" });
            }}
          >
            تعديل الموقع
          </Link>
          <Link
            href={`/edit-listing/v2/${listing.id}?type=data&step=general`}
            passHref
            onClick={() => {
              handleSelectEditOption("modify_details");
              trackStartEditListing({ target: "edit_details" });
            }}
          >
            تعديل التفاصيل
          </Link>
          {!listing?.daily_rentable && (
            <button
              onClick={(e) => {
                trackStartEditListing({ target: "remove_ad" });
                handleCloseListing(e, { id: listing.id });
              }}
              style={{ color: "red" }}
            >
              حذف الإعلان
            </button>
          )}
          {!isPaused && (
            <button
              onClick={() => {
                handlePauseListing({ id: listing.id });
              }}
              style={{ color: "red" }}
            >
              إيقاف الإعلان
            </button>
          )}
        </div>
      }
      onOpen={() => console.log("open")}
      onClose={() => console.log("close")}
    >
      <button
        onPointerDown={() => {
          // Use pointer down so it fires before DropDown possibly swallows click event of child components
          handleEditOptionsClick();
        }}
      >
        <span>
          <Icon width={15} height={15} name="Edit-listing" />
        </span>
        <span> تعديل </span>
      </button>
    </DropDown>
  );
}
